import './App.css';
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import Header from './components/Header';
import Hero from './components/Hero';
import Services from './components/Services';
import About from './components/About';
import Stats from './components/Stats';
import Portfolio from './components/Portfolio';
import Testimonials from './components/Testimonials';
import WhyChooseUs from './components/WhyChooseUs';
import Team from './components/Team';
import Contact from './components/Contact';
import Footer from './components/Footer';
import AppDevelopment from './pages/AppDevelopment';
import ContactPage from './pages/Contact';
import AboutPage from './pages/AboutPage';
import PortfolioPage from './pages/PortfolioPage';
import ProjectDetails from './pages/ProjectDetails';
import BlogsPage from './pages/BlogsPage';
import BlogDetails from './pages/BlogDetails';
import ServicesAdmin from './pages/admin/ServicesAdmin';
import TestimonialsAdmin from './pages/admin/TestimonialsAdmin';
import PortfoliosAdmin from './pages/admin/PortfoliosAdmin';
import BlogsAdmin from './pages/admin/BlogsAdmin';
import BlogTopicsAdmin from './pages/admin/BlogTopicsAdmin';
import ContactQueriesAdmin from './pages/admin/ContactQueriesAdmin';
import QuoteRequestsAdmin from './pages/admin/QuoteRequestsAdmin';
import SettingsAdmin from './pages/admin/SettingsAdmin';
import AdminLogin from './pages/admin/AdminLogin';
import { getStoredAdminRoute } from './constants/adminNavigation';
import AdminRoute from './routes/AdminRoute';
import { AdminAccessProvider } from './context/AdminAccessContext';
import ServicesPage from './pages/ServicesPage';

function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

function AnimatedRoutes() {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin');

  const pageVariants = {
    initial: { opacity: 0, y: 12 },
    animate: { opacity: 1, y: 0 }
  };

  const pageTransition = {
    duration: 0.25,
    ease: 'easeInOut'
  };

  const renderPage = (Component) => (
    <motion.main variants={pageVariants} initial="initial" animate="animate" transition={pageTransition}>
      <Component />
    </motion.main>
  );

  return (
    <>
      {!isAdminRoute && <Header />}
      <AnimatePresence initial={false}>
        <Routes location={location} key={location.pathname}>
          <Route path="/" element={renderPage(HomePage)} />
          <Route path="/services" element={renderPage(ServicesPage)} />
          <Route path="/services/:alias" element={renderPage(AppDevelopment)} />
          <Route path="/contact" element={renderPage(ContactPage)} />
          <Route path="/about" element={renderPage(AboutPage)} />
          <Route path="/portfolio" element={renderPage(PortfolioPage)} />
          <Route path="/project/:slug" element={renderPage(ProjectDetails)} />
          <Route path="/blogs" element={renderPage(BlogsPage)} />
          <Route path="/blog/:slug" element={renderPage(BlogDetails)} />
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route
            path="/admin"
            element={(
              <AdminRoute>
                <AdminLandingRedirect />
              </AdminRoute>
            )}
          />
          <Route
            path="/admin/services"
            element={(
              <AdminRoute>
                <ServicesAdmin />
              </AdminRoute>
            )}
          />
          <Route
            path="/admin/testimonials"
            element={(
              <AdminRoute>
                <TestimonialsAdmin />
              </AdminRoute>
            )}
          />
          <Route
            path="/admin/portfolios"
            element={(
              <AdminRoute>
                <PortfoliosAdmin />
              </AdminRoute>
            )}
          />
          <Route
            path="/admin/blogs"
            element={(
              <AdminRoute>
                <BlogsAdmin />
              </AdminRoute>
            )}
          />
          <Route
            path="/admin/blog-topics"
            element={(
              <AdminRoute>
                <BlogTopicsAdmin />
              </AdminRoute>
            )}
          />
          <Route
            path="/admin/contact-queries"
            element={(
              <AdminRoute>
                <ContactQueriesAdmin />
              </AdminRoute>
            )}
          />
          <Route
            path="/admin/quote-requests"
            element={(
              <AdminRoute>
                <QuoteRequestsAdmin />
              </AdminRoute>
            )}
          />
          <Route
            path="/admin/settings"
            element={(
              <AdminRoute>
                <SettingsAdmin />
              </AdminRoute>
            )}
          />
        </Routes>
      </AnimatePresence>
      {!isAdminRoute && <Footer />}
    </>
  );
}

const homeSectionVariants = {
  hidden: { opacity: 0, y: 32, scale: 0.98 },
  visible: { opacity: 1, y: 0, scale: 1 }
};

const homeSectionTransition = (index = 0) => ({
  duration: 0.5,
  ease: 'easeOut',
  delay: index * 0.05
});

const homeSections = [
  { key: 'hero', Component: Hero },
  { key: 'why-choose-us', Component: WhyChooseUs },
  { key: 'stats', Component: Stats },
  { key: 'services', Component: Services },
  { key: 'about', Component: About },
  { key: 'portfolio', Component: Portfolio },
  { key: 'testimonials', Component: Testimonials },
  { key: 'contact', Component: Contact }
];

function HomePage() {
  return (
    <>
      {homeSections.map(({ key, Component }, index) => (
        <motion.section
          key={key}
          variants={homeSectionVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          transition={homeSectionTransition(index)}
        >
          <Component />
        </motion.section>
      ))}
    </>
  );
}

function App() {
  return (
    <AdminAccessProvider>
      <Router>
        <ScrollToTop />
        <div className="App">
          <AnimatedRoutes />
        </div>
      </Router>
    </AdminAccessProvider>
  );
}

const AdminLandingRedirect = () => {
  const navigate = useNavigate();

  useEffect(() => {
    const target = getStoredAdminRoute();
    navigate(target, { replace: true });
  }, [navigate]);

  return null;
};

export default App;
